import { Component, input, signal, computed, inject } from '@angular/core';
import { FieldChangesAction } from '../../../services/ai-panel/ai-action.types';
import { AiPanelService } from '../../../services/ai-panel/ai-panel.service';

@Component({
  selector: 'app-wg-ai-action-field-changes',
  standalone: true,
  template: `
    <div class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#1a2233] overflow-hidden mt-2 shadow-sm">
      <!-- Header -->
      <button
        (click)="expanded.set(!expanded())"
        class="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
        <span class="material-symbols-outlined" style="font-size:16px;color:#f59e0b">edit_note</span>
        <span class="text-xs font-semibold text-slate-800 dark:text-slate-200 flex-1 text-left">{{ action().title }}</span>
        <span class="text-[10px] text-slate-400 font-medium">{{ selectedCount() }}/{{ action().changes.length }}</span>
        <svg class="size-4 text-slate-400 transition-transform duration-200" [class.rotate-180]="expanded()"
             fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/>
        </svg>
      </button>

      @if (expanded()) {
        @if (action().description) {
          <div class="px-3 pb-2 text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            {{ action().description }}
          </div>
        }

        <div class="border-t border-slate-100 dark:border-slate-700/50">
          @for (change of action().changes; track change.field; let i = $index) {
            <div class="px-3 py-2.5 border-b border-slate-50 dark:border-slate-800/50 last:border-b-0"
                 [class.opacity-50]="!change.selected">
              <label class="flex items-start gap-2 cursor-pointer group">
                <input type="checkbox"
                  [checked]="change.selected"
                  (change)="toggleChange(i)"
                  class="mt-0.5 size-3.5 rounded border-slate-300 dark:border-slate-600 text-primary-600 focus:ring-primary-500 cursor-pointer" />
                <div class="flex-1 min-w-0">
                  <div class="text-[11px] font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1">
                    {{ change.fieldLabel }}
                  </div>
                  <div class="flex items-start gap-1.5 mb-0.5">
                    <span class="text-[10px] text-red-400 font-medium shrink-0 mt-px">−</span>
                    @if (change.oldValue) {
                      <span class="text-[11px] text-red-600/70 dark:text-red-400/60 line-through break-words">
                        {{ isLongText(change.oldValue) && !expandedFields().has(change.field + '_old') ? truncate(change.oldValue) : change.oldValue }}
                        @if (isLongText(change.oldValue)) {
                          <button (click)="toggleFieldExpand(change.field + '_old', $event)"
                                  class="text-primary-500 no-underline text-[10px] ml-1">
                            {{ expandedFields().has(change.field + '_old') ? 'manj' : 'več…' }}
                          </button>
                        }
                      </span>
                    } @else {
                      <span class="text-[11px] text-slate-400 italic">(prazen)</span>
                    }
                  </div>
                  <div class="flex items-start gap-1.5">
                    <span class="text-[10px] text-green-500 font-medium shrink-0 mt-px">+</span>
                    <span class="text-[11px] text-green-700 dark:text-green-400 break-words">
                      {{ isLongText(change.newValue) && !expandedFields().has(change.field + '_new') ? truncate(change.newValue) : change.newValue }}
                      @if (isLongText(change.newValue)) {
                        <button (click)="toggleFieldExpand(change.field + '_new', $event)"
                                class="text-primary-500 no-underline text-[10px] ml-1">
                          {{ expandedFields().has(change.field + '_new') ? 'manj' : 'več…' }}
                        </button>
                      }
                    </span>
                  </div>
                  @if (change.reasoning) {
                    <div class="mt-1 text-[10px] text-slate-400 dark:text-slate-500 italic leading-relaxed">
                      {{ change.reasoning }}
                    </div>
                  }
                </div>
              </label>
            </div>
          }
        </div>

        <div class="px-3 py-2.5 border-t border-slate-200 dark:border-slate-700">
          @if (applyResult(); as result) {
            <div class="flex items-center justify-center gap-1.5 py-1.5">
              @if (result.success) {
                <span class="material-symbols-outlined" style="font-size:16px;color:#22c55e">check_circle</span>
                <span class="text-xs text-green-600 dark:text-green-400 font-medium">
                  {{ result.count }} {{ result.count === 1 ? 'sprememba aplicirana' : 'spremembe aplicirane' }}
                </span>
              } @else {
                <span class="material-symbols-outlined" style="font-size:16px;color:#ef4444">error</span>
                <span class="text-xs text-red-600 dark:text-red-400 font-medium">Napaka pri apliciranju</span>
              }
            </div>
          } @else {
            <button
              (click)="apply()"
              [disabled]="selectedCount() === 0"
              class="w-full h-8 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5"
              [class]="selectedCount() > 0
                ? 'bg-primary-600 hover:bg-primary-700 text-white cursor-pointer'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed'">
              <span class="material-symbols-outlined" style="font-size:14px">check</span>
              Apliciraj {{ selectedCount() }} {{ selectedCount() === 1 ? 'spremembo' : 'spremembe' }}
            </button>
          }
        </div>
      }
    </div>
  `
})
export class WgAiActionFieldChangesComponent {
  action = input.required<FieldChangesAction>();
  private aiPanel = inject(AiPanelService);

  expanded = signal(true);
  expandedFields = signal(new Set<string>());
  applyResult = this.aiPanel.lastApplyResult;

  selectedCount = computed(() => {
    return this.action().changes.filter((c: any) => c.selected).length;
  });

  toggleChange(index: number) {
    const a = this.action();
    a.changes[index].selected = !a.changes[index].selected;
    this.expanded.update(v => v);
  }

  isLongText(val: string): boolean {
    return !!val && val.length > 100;
  }

  truncate(val: string): string {
    return val.length > 100 ? val.substring(0, 100) + '…' : val;
  }

  toggleFieldExpand(key: string, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.expandedFields.update(set => {
      const newSet = new Set(set);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  }

  apply() {
    this.aiPanel.applyFieldChanges(this.action().changes);
  }
}
